import { useEffect, useState } from "react";
import SEO from "../../components/SEO";
import { SlCheck } from "react-icons/sl";
import { Dropdown } from "primereact/dropdown";
import { BsArrowRight, BsCurrencyDollar, BsExclamationTriangleFill, BsLightningCharge } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import ProvinceService from "../../services/province.service";
import TownService from "../../services/town.service";
import ParcelService from "../../services/parcel.service";
import AddressInput from "../../components/AddressInput";
import FeatureHighlights from "../../components/home/FeatureHighlights";
import HomeHowItWorks from "../../components/home/HomeHowItWorks";
import TrustedClients from "../../components/home/TrustedClients";
import CustomerReviews from "../../components/home/CustomerReviews";
import DeliveryOptions from "../../components/home/DeliveryOptions";
import HomeCallToAction from "../../components/home/HomeCallToAction";
import HomeStructuredData from "../../components/home/HomeStructuredData";
import { LIFTSEARCH_URL } from "../../constants/links";
import { loadGoogleMaps } from "../../utils/google-maps";


// Limpopo (province 2) is only serviced in these towns.
const LIMPOPO_PERMITTED_TOWNS = ["Giyani", "Malamulele", "Tzaneen", "Polokwane", "Bungeni", "Thohoyandou", "Phalaborwa"];

const limitToPermittedTowns = (provinceId, towns) =>
  provinceId.toString() === "2"
    ? towns.filter((town) => LIMPOPO_PERMITTED_TOWNS.includes(town.name))
    : towns;

export const HomePage = () => {
  const [createOrderState, setCreateOrderState] = useState({
    distance_type: "long", // "short" or "long"
    distance: "long", // distance state field
    departure_province: "",
    destination_province: "",
    departure_town: "",
    destination_town: "",
    departure_address: "",
    destination_address: "",
    parcel_type: "",
    delivery_type: "",
    provinces: [],
    destination_provinces: [],
    departure_towns: [],
    destination_towns: [],
    parcels: [],
    start_prices: ["60", "100", "150"],
    showPackageError: false,
    acceptedProhibitedItems: false,
  });

  const [errors, setErrors] = useState({
    departure_province: "",
    destination_province: "",
    departure_town: "",
    destination_town: "",
    departure_address: "",
    destination_address: "",
    acceptedProhibitedItems: "",
  });

  const [addressTouched, setAddressTouched] = useState({
    departure_address: false,
    destination_address: false,
  });

  const [distanceWarning, setDistanceWarning] = useState({
    departure: "",
    destination: "",
  });

  const [showValidationSummary, setShowValidationSummary] = useState(false);

  const {
    distance_type,
    distance,
    departure_province,
    destination_province,
    departure_town,
    destination_town,
    departure_address,
    destination_address,
    parcel_type,
    delivery_type,
    provinces,
    destination_provinces,
    departure_towns,
    destination_towns,
    parcels,
    start_prices,
    showPackageError,
    acceptedProhibitedItems,
  } = createOrderState;

  const navigate = useNavigate();

  // Gallagher Business Exchange coordinates (Parcelin Midrand warehouse)
  const MIDRAND_COORDS = { lat: -25.9856, lng: 28.1289 };
  const MAX_DISTANCE_KM = 70;

  // Calculate distance using Google Distance Matrix API
  const calculateDistanceGoogle = async (address) => {
    try {
      const service = new window.google.maps.DistanceMatrixService();

      return new Promise((resolve, reject) => {
        service.getDistanceMatrix(
          {
            origins: [`${MIDRAND_COORDS.lat},${MIDRAND_COORDS.lng}`],
            destinations: [address],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
            avoidHighways: false,
            avoidTolls: false,
          },
          (response, status) => {
            if (status === window.google.maps.DistanceMatrixStatus.OK) {
              const element = response.rows[0].elements[0];
              if (element.status === "OK") {
                const distanceKm = element.distance.value / 1000; // Convert meters to km
                resolve(distanceKm);
              } else {
                reject(new Error("Could not calculate distance"));
              }
            } else {
              reject(new Error("Distance Matrix API error"));
            }
          },
        );
      });
    } catch (error) {
      throw error;
    }
  };

  // Fallback: Calculate distance between two coordinates using Haversine formula
  const calculateDistanceHaversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Validate address distance from Midrand
  const validateAddressDistance = async (address, fieldType) => {
    if (!address || address.trim() === "") {
      setDistanceWarning((prev) => ({
        ...prev,
        [fieldType]: "",
      }));
      return true;
    }

    try {
      let distance;

      // Try Google Distance Matrix API first (more accurate for driving distances)
      try {
        await loadGoogleMaps();
        distance = await calculateDistanceGoogle(address);
      } catch (googleError) {
        console.warn(
          "Google Distance Matrix failed, falling back to geocoding:",
          googleError,
        );
        distance = await calculateDistanceFallback(address);
      }

      if (distance > MAX_DISTANCE_KM) {
        const warningMsg = `This address is approximately ${Math.round(distance)}km from our Midrand warehouse (Gallagher Business Exchange, Halfway House). We currently only service areas within ${MAX_DISTANCE_KM}km of Midrand.`;
        setDistanceWarning((prev) => ({
          ...prev,
          [fieldType]: warningMsg,
        }));
        return false;
      } else {
        setDistanceWarning((prev) => ({
          ...prev,
          [fieldType]: "",
        }));
        return true;
      }
    } catch (error) {
      console.error("Error validating address distance:", error);
      // Don't block the user if distance calculation fails
      setDistanceWarning((prev) => ({
        ...prev,
        [fieldType]: "",
      }));
      return true;
    }
  };

  // Fallback distance calculation using geocoding + Haversine
  const calculateDistanceFallback = async (address) => {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=za&limit=1`,
    );
    const data = await response.json();

    if (data && data.length > 0) {
      const { lat, lon } = data[0];
      return calculateDistanceHaversine(
        MIDRAND_COORDS.lat,
        MIDRAND_COORDS.lng,
        parseFloat(lat),
        parseFloat(lon),
      );
    } else {
      throw new Error("Could not geocode address");
    }
  };

  const handleSelectChange = (event) => {
    const { id, value } = event.target;

    if (id === "departure_province") {
      setCreateOrderState((prevState) => ({
        ...prevState,
        departure_town: "",
        destination_province: "",
        destination_town: "",
        destination_towns: [],
      }));
    }

    setCreateOrderState((prevState) => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleRadioButtonChange = (event) => {
    const { name, value } = event;

    const updates = {
      [name]: value,
      showPackageError: false,
    };

    if (name === "parcel_type") {
      const selectedParcel = parcels.find((p) => p.id === value);
      if (
        selectedParcel &&
        selectedParcel.parcel_prices &&
        selectedParcel.parcel_prices.length > 0
      ) {
        const sortedPrices = [...selectedParcel.parcel_prices].sort(
          (a, b) => a.price.value - b.price.value,
        );
        updates.delivery_type = sortedPrices[0].price.id;
      }
    }

    setCreateOrderState((prevState) => ({
      ...prevState,
      ...updates,
    }));
  };

  useEffect(() => {
    let isCurrent = true;
    ProvinceService.index()
      .then((data) => {
        if (isCurrent) setCreateOrderState((prevState) => ({ ...prevState, provinces: data }));
      })
      .catch((error) => console.error("Error fetching provinces:", error));
    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(() => {
    let isCurrent = true;
    ParcelService.index(distance)
      .then((data) => {
        if (isCurrent) setCreateOrderState((prevState) => ({ ...prevState, parcels: data }));
      })
      .catch((error) => console.error("Error fetching parcel types:", error));
    return () => {
      isCurrent = false;
    };
  }, [distance]);

  useEffect(() => {
    setCreateOrderState((prevState) => ({
      ...prevState,
      destination_provinces: departure_province
        ? prevState.provinces.filter(
            (province) => province.id.toString() !== departure_province.toString(),
          )
        : [],
    }));
  }, [departure_province, provinces]);

  useEffect(() => {
    let isCurrent = true;
    const provinceId = distance_type === "short" ? "1" : departure_province;
    if (!provinceId) return undefined;
    TownService.by_province(provinceId)
      .then((data) => {
        if (!isCurrent) return;
        if (distance_type === "short") {
          // Short distance is Gauteng only, so both ends share the same towns.
          setCreateOrderState((prevState) => ({
            ...prevState,
            departure_towns: data,
            destination_towns: data,
          }));
        } else {
          setCreateOrderState((prevState) => ({
            ...prevState,
            departure_towns: limitToPermittedTowns(provinceId, data),
          }));
        }
      })
      .catch((error) => console.error("Error fetching departure towns:", error));
    return () => {
      isCurrent = false;
    };
  }, [departure_province, distance_type]);

  useEffect(() => {
    let isCurrent = true;
    if (distance_type === "short" || !destination_province) return undefined;
    TownService.by_province(destination_province)
      .then((data) => {
        if (!isCurrent) return;
        setCreateOrderState((prevState) => ({
          ...prevState,
          destination_towns: limitToPermittedTowns(destination_province, data),
        }));
      })
      .catch((error) => console.error("Error fetching destination towns:", error));
    return () => {
      isCurrent = false;
    };
  }, [destination_province, distance_type]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const validationErrors = {};

    if (distance_type === "long") {
      if (!departure_province) {
        validationErrors.departure_province =
          "Please select departure province";
      }
      if (!destination_province) {
        validationErrors.destination_province =
          "Please select destination province";
      }
      if (!departure_town) {
        validationErrors.departure_town = "Please select departure town";
      }
      if (!destination_town) {
        validationErrors.destination_town = "Please select destination town";
      }
    } else {
      // Short distance validation
      if (!departure_address || departure_address.trim() === "") {
        validationErrors.departure_address = "Please enter pickup address";
      }
      if (!destination_address || destination_address.trim() === "") {
        validationErrors.destination_address = "Please enter delivery address";
      }

      // Check if there are distance warnings
      if (distanceWarning.departure || distanceWarning.destination) {
        validationErrors.distance =
          "One or more addresses are outside our service area";
      }
    }

    if (!parcel_type) {
      validationErrors.parcel_type = "Please select a package type";
    }

    if (!acceptedProhibitedItems) {
      validationErrors.acceptedProhibitedItems =
        "You must confirm that you are not sending prohibited items";
    }

    // If there are errors, show them and don't submit
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setShowValidationSummary(true);

      if (validationErrors.parcel_type) {
        setCreateOrderState((prevState) => ({
          ...prevState,
          showPackageError: true,
        }));
      }

      // Scroll to first error
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Clear errors and proceed
    setErrors({});
    setShowValidationSummary(false);
    createOrder();
  };

  const createOrder = () => {
    const formData = {
      distance_type,
      distance,
      departure_province: distance_type === "short" ? "1" : departure_province, // Gauteng ID
      destination_province:
        distance_type === "short" ? "1" : destination_province,
      departure_town,
      destination_town,
      departure_address,
      destination_address,
      // For short distance, pass addresses as pickup/drop-off locations
      pickup_location: distance_type === "short" ? departure_address : "",
      drop_off_location: distance_type === "short" ? destination_address : "",
      parcel_type,
      delivery_type,
      provinces,
      departure_towns,
      destination_towns,
      parcels,
    };
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    navigate("/cart", { state: formData });
  };

  return (
    <div className="w-full flex flex-col overflow-x-hidden bg-gradient-to-br from-cyan-100 to-fuchsia-200">
      <SEO path="/" />
      <HomeStructuredData />
      {/* Hero Section */}
      <div className="relative">
        <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-cyan-300/40 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute top-20 right-0 h-96 w-96 rounded-full bg-fuchsia-300/40 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-5 pt-12 pb-20 lg:pt-16 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left Column - Content */}
            <div className="text-center lg:text-left space-y-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl text-purple-900 leading-[1.05]">
                Parcel Delivery, Done Right
              </h1>
              <p className="text-lg md:text-xl text-gray-700 max-w-xl mx-auto lg:mx-0">
                Affordable, eco-friendly parcel delivery across South Africa.
                Same-day and next-day options available.
              </p>

              {/* Quick Stats */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-3 pt-2">
                <div className="flex items-center gap-3 bg-white/80 backdrop-blur rounded-2xl pl-3 pr-5 py-3 shadow-sm ring-1 ring-white">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-600 text-white" aria-hidden="true">
                    <BsCurrencyDollar />
                  </span>
                  <span className="text-left">
                    <span className="block text-xl font-semibold text-purple-700 leading-tight">60%</span>
                    <span className="block text-sm text-gray-600">Cheaper</span>
                  </span>
                </div>
                <div className="flex items-center gap-3 bg-white/80 backdrop-blur rounded-2xl pl-3 pr-5 py-3 shadow-sm ring-1 ring-white">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-fuchsia-500 text-white" aria-hidden="true">
                    <BsLightningCharge />
                  </span>
                  <span className="text-left">
                    <span className="block text-xl font-semibold text-purple-700 leading-tight">Same Day</span>
                    <span className="block text-sm text-gray-600">Delivery</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column - Booking Form */}
            <div className="w-full">
              <form
                className="bg-white rounded-2xl shadow-2xl overflow-hidden"
                onSubmit={handleSubmit}
              >
                {/* Gradient header band */}
                <div className="bg-gradient-to-br from-purple-700 via-purple-600 to-violet-500 px-6 py-5 relative overflow-hidden">
                  <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full" />
                  <div className="absolute -bottom-8 -left-4 w-20 h-20 bg-white/5 rounded-full" />
                  <h2 className="text-2xl text-white relative">
                    Get Instant Quote
                  </h2>
                  <p className="text-purple-200 text-sm mt-0.5 relative">
                    Takes under 60 seconds · No signup needed
                  </p>
                </div>
                <div className="p-6 space-y-5">
                  {/* Distance Type Toggle — Segmented Control */}
                  <div className="flex rounded-xl bg-gray-100 p-1 gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (distance_type === "long") return;
                        setCreateOrderState((prevState) => ({
                          ...prevState,
                          distance_type: "long",
                          distance: "long",
                          departure_province: "",
                          destination_province: "",
                          departure_town: "",
                          destination_town: "",
                          departure_address: "",
                          destination_address: "",
                        }));
                        setErrors({});
                        setShowValidationSummary(false);
                        setAddressTouched({
                          departure_address: false,
                          destination_address: false,
                        });
                        setDistanceWarning({ departure: "", destination: "" });
                      }}
                      className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                        distance_type === "long"
                          ? "bg-white shadow text-purple-700"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Long Distance
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (distance_type === "short") return;
                        setCreateOrderState((prevState) => ({
                          ...prevState,
                          distance_type: "short",
                          distance: "short",
                          departure_province: "1",
                          destination_province: "1",
                          departure_town: "",
                          destination_town: "",
                          departure_address: "",
                          destination_address: "",
                        }));
                        setErrors({});
                        setShowValidationSummary(false);
                        setAddressTouched({
                          departure_address: false,
                          destination_address: false,
                        });
                        setDistanceWarning({ departure: "", destination: "" });
                      }}
                      className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                        distance_type === "short"
                          ? "bg-white shadow text-purple-700"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Short Distance
                    </button>
                  </div>

                  {/* Validation Summary */}
                  {showValidationSummary && Object.keys(errors).length > 0 && (
                    <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 animate-slideDown">
                      <div className="flex items-start gap-3">
                        <BsExclamationTriangleFill
                          className="text-red-500 mt-1 flex-shrink-0"
                          size={20}
                        />
                        <div>
                          <h3 className="text-red-800 mb-1">
                            Please complete all required fields
                          </h3>
                          <ul className="text-sm text-red-700 list-disc list-inside">
                            {Object.entries(errors).map(
                              ([field, error]) =>
                                error && <li key={field}>{error}</li>,
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Long Distance Form */}
                  {distance_type === "long" && (
                    <>
                      {/* From/To Section */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            From <span className="text-red-500">*</span>
                          </label>
                          <div
                            className={
                              errors.departure_province
                                ? "ring-2 ring-red-500 rounded-lg"
                                : ""
                            }
                          >
                            <Dropdown
                              value={departure_province}
                              onChange={(e) => {
                                handleSelectChange({
                                  target: {
                                    id: "departure_province",
                                    value: e.value,
                                  },
                                });
                                setErrors((prev) => ({
                                  ...prev,
                                  departure_province: "",
                                }));
                                setShowValidationSummary(false);
                              }}
                              options={provinces}
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Province *"
                              filter
                              className="w-full"
                              style={{ borderRadius: "0.5rem" }}
                            />
                          </div>
                          {errors.departure_province && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.departure_province}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            To <span className="text-red-500">*</span>
                          </label>
                          <div
                            className={
                              errors.destination_province
                                ? "ring-2 ring-red-500 rounded-lg"
                                : ""
                            }
                          >
                            <Dropdown
                              value={destination_province}
                              onChange={(e) => {
                                handleSelectChange({
                                  target: {
                                    id: "destination_province",
                                    value: e.value,
                                  },
                                });
                                setErrors((prev) => ({
                                  ...prev,
                                  destination_province: "",
                                }));
                                setShowValidationSummary(false);
                              }}
                              options={destination_provinces}
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Province *"
                              filter
                              className="w-full"
                              style={{ borderRadius: "0.5rem" }}
                            />
                          </div>
                          {errors.destination_province && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.destination_province}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div
                            className={
                              errors.departure_town
                                ? "ring-2 ring-red-500 rounded-lg"
                                : ""
                            }
                          >
                            <Dropdown
                              value={departure_town}
                              onChange={(e) => {
                                handleSelectChange({
                                  target: {
                                    id: "departure_town",
                                    value: e.value,
                                  },
                                });
                                setErrors((prev) => ({
                                  ...prev,
                                  departure_town: "",
                                }));
                                setShowValidationSummary(false);
                              }}
                              options={departure_towns}
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Town *"
                              filter
                              className="w-full"
                              style={{ borderRadius: "0.5rem" }}
                            />
                          </div>
                          {errors.departure_town && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.departure_town}
                            </p>
                          )}
                        </div>
                        <div>
                          <div
                            className={
                              errors.destination_town
                                ? "ring-2 ring-red-500 rounded-lg"
                                : ""
                            }
                          >
                            <Dropdown
                              value={destination_town}
                              onChange={(e) => {
                                handleSelectChange({
                                  target: {
                                    id: "destination_town",
                                    value: e.value,
                                  },
                                });
                                setErrors((prev) => ({
                                  ...prev,
                                  destination_town: "",
                                }));
                                setShowValidationSummary(false);
                              }}
                              options={destination_towns}
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Town *"
                              filter
                              className="w-full"
                              style={{ borderRadius: "0.5rem" }}
                            />
                          </div>
                          {errors.destination_town && (
                            <p className="text-red-600 text-xs mt-1">
                              {errors.destination_town}
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Short Distance Form */}
                  {distance_type === "short" && (
                    <>
                      {/* Info Banner */}
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <p className="text-sm text-purple-800">
                          <strong>Short Distance Delivery:</strong> Available
                          within 70km of our Midrand warehouse (Gallagher Business Exchange)
                        </p>
                      </div>

                      {/* Pickup Address */}
                      <AddressInput
                        id="departure_address"
                        name="departure_address"
                        value={departure_address}
                        onChange={(e) => {
                          setCreateOrderState((prevState) => ({
                            ...prevState,
                            departure_address: e.target.value,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            departure_address: "",
                          }));
                          setShowValidationSummary(false);
                          // Validate distance after a delay
                          setTimeout(() => {
                            validateAddressDistance(
                              e.target.value,
                              "departure",
                            );
                          }, 1000);
                        }}
                        onBlur={() => {
                          setAddressTouched((prev) => ({
                            ...prev,
                            departure_address: true,
                          }));
                          validateAddressDistance(
                            departure_address,
                            "departure",
                          );
                        }}
                        label="Pickup Address"
                        placeholder="Enter pickup address"
                        error={errors.departure_address}
                        touched={addressTouched.departure_address}
                        showRequired={true}
                      />

                      {/* Distance Warning for Pickup */}
                      {distanceWarning.departure && (
                        <div className="bg-orange-50 border-2 border-orange-500 rounded-lg p-3 animate-slideDown">
                          <div className="flex items-start gap-2">
                            <BsExclamationTriangleFill
                              className="text-orange-500 mt-0.5 flex-shrink-0"
                              size={16}
                            />
                            <p className="text-sm text-orange-800">
                              {distanceWarning.departure}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Delivery Address */}
                      <AddressInput
                        id="destination_address"
                        name="destination_address"
                        value={destination_address}
                        onChange={(e) => {
                          setCreateOrderState((prevState) => ({
                            ...prevState,
                            destination_address: e.target.value,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            destination_address: "",
                          }));
                          setShowValidationSummary(false);
                          // Validate distance after a delay
                          setTimeout(() => {
                            validateAddressDistance(
                              e.target.value,
                              "destination",
                            );
                          }, 1000);
                        }}
                        onBlur={() => {
                          setAddressTouched((prev) => ({
                            ...prev,
                            destination_address: true,
                          }));
                          validateAddressDistance(
                            destination_address,
                            "destination",
                          );
                        }}
                        label="Delivery Address"
                        placeholder="Enter delivery address"
                        error={errors.destination_address}
                        touched={addressTouched.destination_address}
                        showRequired={true}
                      />

                      {/* Distance Warning for Delivery */}
                      {distanceWarning.destination && (
                        <div className="bg-orange-50 border-2 border-orange-500 rounded-lg p-3 animate-slideDown">
                          <div className="flex items-start gap-2">
                            <BsExclamationTriangleFill
                              className="text-orange-500 mt-0.5 flex-shrink-0"
                              size={16}
                            />
                            <p className="text-sm text-orange-800">
                              {distanceWarning.destination}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Package Selection */}
                  <div id="package-section">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Package Size{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    {showPackageError && (
                      <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-2 rounded-lg text-sm mb-3">
                        Please select a package type
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {parcels &&
                        parcels.map((parcel, index) => (
                          <div
                            key={parcel.id}
                            className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                              createOrderState.parcel_type === parcel.id
                                ? "border-purple-600 bg-purple-50"
                                : "border-gray-200 hover:border-purple-300"
                            }`}
                            onClick={() =>
                              handleRadioButtonChange({
                                name: "parcel_type",
                                value: parcel.id,
                              })
                            }
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-semibold text-gray-800">
                                {parcel.package_type}
                              </div>
                              {createOrderState.parcel_type === parcel.id && (
                                <SlCheck
                                  size={20}
                                  className="text-purple-600"
                                />
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              From R{parcel.parcel_prices[0].price.value}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {parcel.max_height} x {parcel.max_width}{" "}
                              {parcel.max_weight}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Prohibited Items Checkbox */}
                  <div className="border-t border-gray-200 pt-3">
                    <div
                      className={`flex items-start gap-2 p-3 rounded-lg transition-all ${
                        errors.acceptedProhibitedItems
                          ? "bg-red-50 border border-red-400"
                          : "bg-gray-50 border border-gray-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        id="acceptedProhibitedItems"
                        checked={acceptedProhibitedItems}
                        onChange={(e) => {
                          setCreateOrderState((prevState) => ({
                            ...prevState,
                            acceptedProhibitedItems: e.target.checked,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            acceptedProhibitedItems: "",
                          }));
                          setShowValidationSummary(false);
                        }}
                        className="mt-0.5 h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                      />
                      <label
                        htmlFor="acceptedProhibitedItems"
                        className="flex-1 cursor-pointer text-xs text-gray-700"
                      >
                        I confirm I'm not sending prohibited items (weapons,
                        explosives, illegal substances, etc.){" "}
                        <span className="text-red-500">*</span>
                      </label>
                    </div>
                    {errors.acceptedProhibitedItems && (
                      <p className="text-red-600 text-xs mt-1.5 ml-1">
                        {errors.acceptedProhibitedItems}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-semibold py-4 rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group"
                  >
                    Get Quote & Continue
                    <BsArrowRight
                      size={18}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </button>
                  <p className="text-center text-sm text-gray-600">
                    Want to get a lift instead?{" "}
                    <a
                      href={LIFTSEARCH_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-purple-700 underline decoration-purple-300 decoration-2 underline-offset-4 hover:text-purple-900"
                    >
                      Find a lift
                    </a>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <FeatureHighlights />
      <HomeHowItWorks />
      <TrustedClients />
      <CustomerReviews />
      <DeliveryOptions />
      <HomeCallToAction />
    </div>
  );
};
